import React from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { YStack, XStack, H4, SizableText, Button } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import getDaysAgo from '@utils/getDaysAgo';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';

interface Topic {
  _id: string;
  title: string;
  description: string;
  icon?: string;
  color?: string;
  threadsCount: number;
  postsCount: number;
  lastActivity?: string;
  lastPost?: {
    user: {
      name: string;
      avatar?: string;
    };
    createdAt: string;
  };
  isSticky?: boolean;
  isLocked?: boolean;
  createdBy: {
    _id: string;
    name: string;
    surname: string;
    avatar?: string;
  };
  createdAt: string;
}

interface TopicsListProps {
  topics: Topic[];
  loading: boolean;
  onTopicSelect: (topicId: string) => void;
  selectedTopic?: string | null;
  compact?: boolean;
}

const TopicItem: React.FC<{
  topic: Topic;
  onPress: () => void;
  isSelected?: boolean;
  compact?: boolean;
}> = ({ topic, onPress, isSelected = false, compact = false }) => {
  const theme = themeSelector();
  
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    selectedBackground: theme ? '#e0f2fe' : '#1e3a8a',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: topic.color || '#3b82f6',
  };

  const getTopicIcon = () => {
    if (topic.icon) {
      return <MaterialCommunityIcons name={topic.icon as any} size={24} color={colors.accent} />;
    }
    return <MaterialCommunityIcons name="forum" size={24} color={colors.accent} />;
  };

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      delay={compact ? 0 : 100}
    >
      <Pressable
        onPress={onPress}
        style={({ pressed }) => ({
          backgroundColor: isSelected 
            ? colors.selectedBackground 
            : pressed 
              ? colors.cardBackground 
              : colors.background,
          marginHorizontal: compact ? 8 : 16,
          marginVertical: compact ? 4 : 8,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: isSelected ? colors.accent : colors.border,
          padding: compact ? 12 : 16,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 2,
          opacity: pressed ? 0.8 : 1,
        })}
      >
        <XStack gap="$3" alignItems="flex-start">
          {/* Topic Icon */}
          <View style={{
            width: compact ? 36 : 48,
            height: compact ? 36 : 48,
            backgroundColor: `${colors.accent}15`,
            borderRadius: compact ? 18 : 24,
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            {getTopicIcon()}
          </View>

          {/* Content */}
          <YStack flex={1} gap="$1">
            <XStack justifyContent="space-between" alignItems="flex-start">
              <H4 
                color={colors.text} 
                numberOfLines={compact ? 1 : 2}
                fontSize={compact ? "$4" : "$5"}
                fontWeight="600"
                flex={1}
              >
                {topic.title}
                {topic.isSticky && (
                  <MaterialIcons 
                    name="push-pin" 
                    size={16} 
                    color={colors.accent} 
                    style={{ marginLeft: 8 }}
                  />
                )}
                {topic.isLocked && (
                  <MaterialIcons 
                    name="lock" 
                    size={16} 
                    color={colors.textSecondary} 
                    style={{ marginLeft: 4 }}
                  />
                )}
              </H4>
            </XStack>

            {!compact && (
              <SizableText 
                color={colors.textSecondary} 
                size="$3" 
                numberOfLines={2}
                lineHeight={18}
              >
                {topic.description}
              </SizableText>
            )}

            {/* Stats Row */}
            <XStack 
              justifyContent="space-between" 
              alignItems="center" 
              marginTop={compact ? "$1" : "$2"}
            >
              <XStack gap="$3" alignItems="center">
                <XStack gap="$1" alignItems="center">
                  <MaterialCommunityIcons 
                    name="message-text" 
                    size={14} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$2">
                    {topic.threadsCount}
                  </SizableText>
                </XStack>

                <XStack gap="$1" alignItems="center">
                  <MaterialCommunityIcons 
                    name="comment" 
                    size={14} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$2">
                    {topic.postsCount}
                  </SizableText>
                </XStack>
              </XStack>

              {topic.lastActivity && (
                <SizableText color={colors.textSecondary} size="$2">
                  {getDaysAgo(topic.lastActivity)}
                </SizableText>
              )}
            </XStack>

            {/* Last Post Info */}
            {topic.lastPost && !compact && (
              <XStack 
                marginTop="$2" 
                padding="$2" 
                backgroundColor={colors.cardBackground}
                borderRadius="$2"
                alignItems="center"
                gap="$2"
              >
                <MaterialCommunityIcons 
                  name="account-circle" 
                  size={16} 
                  color={colors.textSecondary} 
                />
                <SizableText color={colors.textSecondary} size="$2" flex={1}>
                  Last by {topic.lastPost.user.name} • {getDaysAgo(topic.lastPost.createdAt)}
                </SizableText>
              </XStack>
            )}
          </YStack>
        </XStack>
      </Pressable>
    </Animatable.View>
  );
};

const TopicsList: React.FC<TopicsListProps> = ({
  topics,
  loading,
  onTopicSelect,
  selectedTopic,
  compact = false
}) => {
  const theme = themeSelector();
  
  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
  };

  if (loading) {
    return (
      <YStack padding="$4" gap="$4" alignItems="center">
        <MaterialCommunityIcons 
          name="loading" 
          size={32} 
          color={colors.textSecondary} 
        />
        <SizableText color={colors.textSecondary}>Loading topics...</SizableText>
      </YStack>
    );
  }

  if (!topics || topics.length === 0) {
    return (
      <YStack 
        padding="$6" 
        gap="$4" 
        alignItems="center"
        justifyContent="center"
        flex={1}
      >
        <MaterialCommunityIcons 
          name="forum-outline" 
          size={64} 
          color={colors.textSecondary} 
        />
        <H4 color={colors.text} textAlign="center">No Topics Yet</H4>
        <SizableText 
          color={colors.textSecondary} 
          textAlign="center"
          maxWidth={280}
        >
          Be the first to create a discussion topic in this community!
        </SizableText>
      </YStack>
    );
  }

  const renderTopic = ({ item }: { item: Topic }) => (
    <TopicItem
      topic={item}
      onPress={() => onTopicSelect(item._id)}
      isSelected={selectedTopic === item._id}
      compact={compact}
    />
  );

  return (
    <FlatList
      data={topics}
      renderItem={renderTopic}
      keyExtractor={(item) => item._id}
      contentContainerStyle={{
        paddingVertical: compact ? 8 : 16,
        backgroundColor: colors.background,
        flexGrow: 1,
      }}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews
      maxToRenderPerBatch={10}
      windowSize={10}
    />
  );
};

export default TopicsList;
